import {
  IKey,
  IKeySideLogoSize,
  IKeySideName,
} from "../../../../store/keysTypes";
import getKeyImage from "../../../../tools/getGetKeyImage";
import { toPng } from "html-to-image";
import { useEffect, useRef } from "react";

type ContainerConfig = {
  width: number;
  height: number;
  left: number;
  top: number;
};

const containerConfigs: Record<
  string,
  { front: ContainerConfig; back: ContainerConfig }
> = {
  "fa-head": {
    front: { width: 102, height: 56, left: 40, top: 56 },
    back: { width: 102, height: 56, left: 47, top: 56 },
  },
  flafa: {
    front: { width: 103, height: 56, left: 39, top: 56 },
    back: { width: 103, height: 56, left: 38, top: 56 },
  },
  "ov-head": {
    front: { width: 102, height: 47, left: 42, top: 63 },
    back: { width: 102, height: 47, left: 42, top: 63 },
  },
  bohrmulden: {
    front: { width: 102, height: 56, left: 39, top: 56 },
    back: { width: 102, height: 56, left: 41, top: 56 },
  },
};

const originalContainerConfigs: Record<
  string,
  { front: ContainerConfig; back: ContainerConfig }
> = {
  "abu-17d": {
    front: { width: 124, height: 57, left: 31, top: 86 },
    back: { width: 124, height: 57, left: 33, top: 86 },
  },
  "abu-86d": {
    front: { width: 100, height: 52, left: 45, top: 70 },
    back: { width: 100, height: 52, left: 43, top: 70 },
  },
  "abu-93": {
    front: { width: 120, height: 60, left: 34, top: 96 },
    back: { width: 120, height: 60, left: 34, top: 96 },
  },
  "bk-7d": {
    front: { width: 90, height: 70, left: 50, top: 49 },
    back: { width: 90, height: 70, left: 49, top: 49 },
  },
  "bur-23": {
    front: { width: 120, height: 50, left: 35, top: 68 },
    back: { width: 120, height: 50, left: 35, top: 68 },
  },
  "ce-53d": {
    front: { width: 120, height: 40, left: 35, top: 78 },
    back: { width: 120, height: 40, left: 35, top: 78 },
  },
  "dom-21": {
    front: { width: 120, height: 50, left: 35, top: 60 },
    back: { width: 120, height: 50, left: 35, top: 60 },
  },
  "haf-2d": {
    front: { width: 120, height: 60, left: 34, top: 96 },
    back: { width: 120, height: 60, left: 34, top: 96 },
  },
  "is-15": {
    front: { width: 120, height: 60, left: 34, top: 96 },
    back: { width: 120, height: 60, left: 34, top: 96 },
  },
  "jng-1d": {
    front: { width: 120, height: 50, left: 34, top: 70 },
    back: { width: 120, height: 50, left: 34, top: 70 },
  },
  "re-6d": {
    front: { width: 120, height: 60, left: 34, top: 104 },
    back: { width: 120, height: 60, left: 34, top: 104 },
  },
  "u-15d": {
    front: { width: 120, height: 60, left: 34, top: 96 },
    back: { width: 120, height: 60, left: 34, top: 96 },
  },
  "wil-66d": {
    front: { width: 110, height: 40, left: 41, top: 74 },
    back: { width: 110, height: 40, left: 37, top: 74 },
  },
  "win-22": {
    front: { width: 120, height: 60, left: 33, top: 66 },
    back: { width: 120, height: 60, left: 35, top: 66 },
  },
};

export const SummaryKeyImage = (props: {
  _key: IKey;
  side: string;
  profile: any;
  onScreenShot: (side: IKeySideName, value: string) => void;
}) => {
  const { _key, side, profile, onScreenShot } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const screenShotTaken = useRef(false);
  const src = getKeyImage({
    profile,
    blank: _key.blank,
    side,
    customization: "summary",
  });
  const keySide = _key[side.toLowerCase() as "front" | "back"];
  const isOriginal = _key.blank === "original-head";
  const isFLA = profile === "flafa";
  const isBohrMulden = [
    "abu-91fa",
    "basi-bmfa",
    "basi-vbfa",
    "ci-48fa",
  ].includes(profile);
  const container = isOriginal
    ? originalContainerConfigs?.[_key.value]?.[side as "front" | "back"] ??
      containerConfigs["fa-head"][side as "front" | "back"]
    : isFLA
    ? containerConfigs["flafa"][side as "front" | "back"]
    : isBohrMulden
    ? containerConfigs["bohrmulden"][side as "front" | "back"]
    : containerConfigs[_key.blank][side as "front" | "back"];

  useEffect(() => {
    if (screenShotTaken.current) return;
    screenShotTaken.current = true;
    if (containerRef.current) {
      toPng(containerRef.current, { cacheBust: true }).then((result) => {
        onScreenShot(side as IKeySideName, result);
      });
    }
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "188px" }}>
      <div
        style={{
          position: "absolute",
          width: container.width + "px",
          height: container.height + "px",
          left: container.left + "px",
          top: container.top + "px",
          zIndex: 1,
          overflow: "hidden",
          // border: "1px solid red",
        }}
      >
        {keySide.logo ? (
          <img
            src={keySide.logo}
            style={{
              objectFit: "contain",
              height: "100%",
              margin: "0 auto",
              transform: `scale(${
                keySide.logoSize === IKeySideLogoSize.BIG
                  ? 1
                  : keySide.logoSize === IKeySideLogoSize.MEDIUM
                  ? 0.7
                  : 0.5
              })`,
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {keySide.text.map((text, index) => (
              <div
                key={index}
                style={{
                  fontSize: `${keySide.size[text.name] / 2.2}px`,
                  fontFamily: keySide.font,
                  color: "white",
                  textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                  margin: "0px 0",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    width: 0,
                    overflow: "hidden",
                    display: "inline-block",
                  }}
                >
                  I
                </span>
                {text.value}
              </div>
            ))}
          </div>
        )}
      </div>
      <img src={src.image} />
    </div>
  );
};
